"use client";

import React, { useState } from "react";
import { Search, FileText, Code as Code2, TrendingUp, CircleCheck as CheckCircle2, ArrowRight, UserCheck, Target, Sparkles, ChevronRight, ClipboardList } from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
interface ProcessStep {
  id: string;
  number: string;
  title: string;
  badge: string;
  summary: string;
  deliverables: string[];
  collaborationNote: React.ReactNode;
}
const processSteps: ProcessStep[] = [{
  id: "step-1",
  number: "01",
  title: "Discovery & Commercial Objectives",
  badge: "Foundation & Context",
  summary: "A thorough review of your business model, products, services, target accounts, and existing digital and sales setup. I examine sales cycle length, current lead sources, revenue targets, and what commercial success looks like for your team.",
  deliverables: ["Review of business model, core offerings, and target accounts", "Analysis of current enquiry sources and sales cycle dynamics", "Definition of commercial targets and qualified lead criteria", "Evaluation of existing digital setup and CRM integration points"],
  collaborationNote: "I discuss commercial priorities directly with your leadership and sales stakeholders to ensure the search strategy targets genuine buyers rather than casual researchers."
}, {
  id: "step-2",
  number: "02",
  title: "Technical & Competitor Audit",
  badge: "Technical & Market Health",
  summary: "A detailed examination of site health, crawlability, indexation, analytics setup, and core landing pages, paired with a high-level competitor and search landscape review.",
  deliverables: ["Comprehensive technical crawlability and indexation review", "Website architecture and internal linking evaluation", "Commercial competitor search landscape analysis", "Analytics tracking and conversion goal verification"],
  collaborationNote: <span>
        I identify technical hurdles preventing discovery and highlight competitor content gaps. For deeper architectural work, see my dedicated{" "}
        <Link to="/SeoAuditsPage" className="text-primary underline underline-offset-4 hover:text-primary/80">
          SEO audit
        </Link>{" "}
        service.
      </span>
}, {
  id: "step-3",
  number: "03",
  title: "Keyword Research & SEO Strategy",
  badge: "Intent & Mapping",
  summary: "Mapping non-branded, buyer-intent search queries to specific service pages, industries, use cases, and buyer journey stages, backed by insights from your sales and marketing teams.",
  deliverables: ["High-intent commercial query mapping across buyer journey stages", "Alignment with sales team feedback on common procurement questions", "Identification of industry-specific and use-case search clusters", "Prioritised roadmap of priority pages, topics, and technical updates"],
  collaborationNote: "Keyword research prioritises commercial intent over raw search volume, focusing on terms that decision-makers and procurement leads use during supplier evaluation."
}, {
  id: "step-4",
  number: "04",
  title: "Implementation & Content Optimisation",
  badge: "Execution & Collaboration",
  summary: "Working directly alongside your in-house team, developers, and writers to optimise priority commercial landing pages, brief focused content, and guide technical refinements.",
  deliverables: ["On-page optimisation for core service and solution landing pages", "Structured briefs for comparison, evaluation, and industry content", "Clear technical guidance and specifications for your web team", "Refinements to lead capture, page structure, and call-to-action placement"],
  collaborationNote: "Recommendations are pragmatic and calibrated to your internal capacity, ensuring updates move into production smoothly without overwhelming your team."
}, {
  id: "step-5",
  number: "05",
  title: "Measurement & Continuous Improvement",
  badge: "Refinement & Pipeline",
  summary: "Ongoing monitoring through Google Search Console, analytics platforms, and CRM feedback to evaluate commercial visibility, landing-page performance, and enquiry quality.",
  deliverables: ["Tracking non-branded commercial query visibility and impressions", "Reviewing landing-page engagement and organic enquiry quality", "Incorporating sales team feedback to refine keyword priorities", "Iterative adjustments to content and architecture based on real performance"],
  collaborationNote: "I review findings with you in plain commercial language, continuously refining strategy to support your sales pipeline."
}];
const expectationsList = [{
  title: "Plain-English Reporting",
  description: "Clear, commercial summaries focusing on relevant search visibility, enquiry trends, and landing-page performance without confusing jargon."
}, {
  title: "Tied to Commercial Goals",
  description: "Every keyword, landing page, and technical recommendation is aligned directly with your target accounts, services, and sales priorities."
}, {
  title: "Adapted to Internal Capacity",
  description: "Pragmatic, prioritised guidance designed to work alongside your in-house marketers, developers, and copywriters without friction."
}, {
  title: "Realistic, Honest Expectations",
  description: "A principled approach with no artificial guarantees of rankings, leads, or citations, focusing on disciplined, sustainable search growth."
}];
export default function Workflow() {
  const [activeTab, setActiveTab] = useState(0);
  const shouldReduceMotion = useReducedMotion();
  const activeStep = processSteps[activeTab];
  return <section id="b2bseoprocessworkflow" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary font-medium tracking-wide">
            <Sparkles className="w-3.5 h-3.5 mr-1.5 text-accent" />
            B2B SEO PROCESS
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default">
            My five-stage B2B SEO consulting process
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            My process is flexible and adapts to your internal capacity and commercial priorities. Each stage builds on the previous one to align organic search with your sales cycle and target buyers.
          </p>
        </div>

        <div className="mb-12">
          <div className="hidden lg:grid grid-cols-5 gap-3 bg-muted/50 p-2 rounded-2xl border border-border">
            {processSteps.map((step, index) => {
            const isActive = activeTab === index;
            return <Button key={step.id} data-index={index} variant={isActive ? "default" : "ghost"} onClick={() => setActiveTab(index)} className={`relative flex flex-col items-start p-4 h-auto rounded-xl text-left transition-all ${isActive ? "bg-primary text-primary-foreground shadow-md" : "hover:bg-background/80 text-muted-foreground hover:text-foreground"}`}>
                  <div className="flex items-center justify-between w-full mb-2">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-md ${isActive ? "bg-primary-foreground/20 text-primary-foreground" : "bg-background border border-border text-foreground"}`}>
                      Stage {step.number}
                    </span>
                    <ClipboardList className={`w-3.5 h-3.5 ${isActive ? "text-primary-foreground/80" : "text-muted-foreground"}`} />
                  </div>
                  <span className="font-semibold text-sm line-clamp-2 font-default">
                    {step.title}
                  </span>
                </Button>;
          })}
          </div>

          <div className="flex lg:hidden overflow-x-auto gap-3 pb-4 scrollbar-none snap-x">
            {processSteps.map((step, index) => {
            const isActive = activeTab === index;
            return <Button key={step.id} data-index={index} variant={isActive ? "default" : "outline"} onClick={() => setActiveTab(index)} className={`snap-start shrink-0 min-w-[220px] flex flex-col items-start p-4 h-auto rounded-xl text-left ${isActive ? "bg-primary text-primary-foreground" : "bg-card text-foreground"}`}>
                  <div className="flex items-center justify-between w-full mb-1">
                    <span className="text-xs font-bold">Stage {step.number}</span>
                  </div>
                  <span className="font-semibold text-sm font-default">{step.title}</span>
                </Button>;
          })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-20">
          <Card className="lg:col-span-8 border-border bg-card shadow-sm rounded-2xl overflow-hidden">
            <CardHeader className="bg-muted/30 border-b border-border p-6 sm:p-8">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-lg">
                    {activeStep.number}
                  </div>
                  <div>
                    <CardTitle className="text-xl sm:text-2xl font-semibold text-foreground font-default">
                      {activeStep.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground mt-0.5">
                      Stage {activeStep.number} of my B2B SEO consulting framework
                    </CardDescription>
                  </div>
                </div>
                <Badge variant="secondary" className="bg-secondary text-secondary-foreground font-medium px-3 py-1">
                  {activeStep.badge}
                </Badge>
              </div>
            </CardHeader>

            <CardContent className="p-6 sm:p-8 space-y-6">
              <AnimatePresence mode="wait">
                <motion.div key={activeStep.id} initial={shouldReduceMotion ? {
                opacity: 1
              } : {
                opacity: 0,
                y: 12
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={shouldReduceMotion ? {
                opacity: 1
              } : {
                opacity: 0,
                y: -12
              }} transition={{
                duration: 0.25
              }} className="space-y-6">
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                      Stage Objective
                    </h3>
                    <p className="text-base text-foreground leading-relaxed font-default">
                      {activeStep.summary}
                    </p>
                  </div>

                  <Separator />

                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                      Key Deliverables & Action Items
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {activeStep.deliverables.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-2.5 p-3 rounded-lg bg-background border border-border/60">
                          <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                          <span className="text-sm text-foreground font-default">{item}</span>
                        </div>)}
                    </div>
                  </div>

                  <Separator />

                  <div className="bg-muted/20 p-4 rounded-xl border border-border/50">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">
                      How I Work With You
                    </h3>
                    <p className="text-sm text-foreground font-default leading-relaxed">
                      {activeStep.collaborationNote}
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </CardContent>
          </Card>

          <div className="lg:col-span-4 space-y-6">
            <Card className="border-border bg-card shadow-sm rounded-2xl">
              <CardHeader className="pb-4">
                <div className="w-10 h-10 rounded-xl bg-accent/15 text-accent flex items-center justify-center mb-3">
                  <UserCheck className="w-5 h-5" />
                </div>
                <CardTitle className="text-lg font-semibold text-foreground font-default">
                  Working Directly With Yassine
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  I lead and deliver the strategy myself across every stage.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                <div className="flex items-start gap-3 text-sm text-foreground font-default">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <span>Direct consultant access throughout research, strategy, and execution</span>
                </div>
                <div className="flex items-start gap-3 text-sm text-foreground font-default">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <span>Recommendations shaped around your sales cycle and target accounts</span>
                </div>
                <div className="flex items-start gap-3 text-sm text-foreground font-default">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <span>Pragmatic technical guidance aligned with your internal capacity</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-primary/20 bg-primary/5 shadow-sm rounded-2xl p-6">
              <div className="flex items-center gap-2 text-primary font-semibold text-sm mb-2 font-default">
                <Target className="w-4 h-4" />
                <span>Discuss your B2B search strategy</span>
              </div>
              <p className="text-xs text-muted-foreground mb-4 font-default leading-relaxed">
                Book a free SEO consultation to explore how organic search can support your commercial pipeline and sales objectives.
              </p>
              <div className="space-y-2">
                <Button asChild variant="default" className="w-full justify-between group">
                  <Link to="/ContactPage">
                    <span>Book a Free SEO Consultation</span>
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full justify-between">
                  <Link to="/CaseStudiesPage">
                    <span>Explore My Case Studies</span>
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </Card>
          </div>
        </div>

        <div className="border-t border-border pt-16">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <h3 className="text-2xl font-semibold tracking-tight text-foreground font-default">
              What you can expect throughout the process
            </h3>
            <p className="text-sm text-muted-foreground mt-2 font-default">
              Structured collaboration based on transparent communication and realistic expectations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {expectationsList.map((item, index) => <Card key={index} data-index={index} className="border-border bg-card shadow-sm rounded-xl p-6">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold text-sm mb-4">
                  0{index + 1}
                </div>
                <h3 className="text-base font-semibold text-foreground mb-2 font-default">
                  {item.title}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed font-default">
                  {item.description}
                </p>
              </Card>)}
          </div>
        </div>
      </div>
    </section>;
}